import React, { useState, useEffect } from "react";
import { useAnimation } from "./AnimationContext";
import {
  Eye,
  Lock,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Layers,
  Pencil,
  X,
} from "lucide-react";
import { animationConfig } from "./animations/animationConfig";

export default function AnimatePropertiesPanel() {
  const {
    elements,
    selectedElement,
    interactions,
    selectedActionId,
    setSelectedActionId,
    addAction,
    deleteAction,
    updateAction,
    updateElement,
    updateElementState,
    setSelectedElementId,
  } = useAnimation();

  const [open, setOpen] = useState({
    posSize: false,
    animation: false,
    trigger: false,
    action: false,
  });
  const [selectedEvent, setSelectedEvent] = useState("slide_start");
  const [selectedTarget, setSelectedTarget] = useState("timeline");
  const [selectedActionType, setSelectedActionType] = useState("move");
  const [actionValues, setActionValues] = useState(
    animationConfig.move.defaults,
  );
  const [duration, setDuration] = useState("1000");
  const [ease, setEase] = useState("Power2.out");

  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const inputCls =
    "w-full border border-gray-200 rounded p-1.5 focus:border-purpleColor focus:outline-none text-xs";

  const editingAction = (() => {
    if (!selectedActionId) return null;
    for (const int of interactions) {
      const found = int.actions.find((a) => a.id === selectedActionId);
      if (found) return { ...found, event: int.event, target: int.target };
    }
    return null;
  })();

  useEffect(() => {
    setActionValues(animationConfig[selectedActionType].defaults);
  }, [selectedActionType]);

  useEffect(() => {
    if (!editingAction) return;

    setSelectedActionType(editingAction.type);

    setDuration(String(Math.round((editingAction.duration || 1) * 1000)));

    setEase(editingAction.ease || "Power2.out");

    setActionValues({
      ...animationConfig[editingAction.type].defaults,
      ...editingAction.to,
    });

    if (!open.action) {
      setOpen((prev) => ({
        ...prev,
        action: true,
      }));
    }
  }, [selectedActionId]);

  const buildToValue = () => {
    const config = animationConfig[selectedActionType];

    return config.fields.reduce((acc, field) => {
      acc[field.key] = parseFloat(actionValues[field.key]) || 0;

      return acc;
    }, {});
  };

  if (!selectedElement) {
    return (
      <div className="p-4 flex flex-col h-full">
        <div className="flex flex-col items-center justify-center flex-1 text-center text-gray-400">
          <Layers className="w-8 h-8 mb-2 opacity-30" />
          <p className="text-sm font-medium text-gray-500">
            No element selected
          </p>
          <p className="text-xs mt-1">
            Click an element on the canvas to configure it
          </p>
        </div>

        {elements.length > 0 && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-[10px] text-gray-400 font-bold uppercase mb-2">
              Elements
            </p>
            <div className="flex flex-col gap-1.5">
              {elements.map((el) => (
                <button
                  key={el.id}
                  onClick={() => setSelectedElementId(el.id)}
                  className="bg-gray-50 hover:bg-purpleColor/10 hover:text-purpleColor text-gray-700 text-xs px-3 py-2 rounded-lg transition text-left truncate"
                >
                  {el.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const handlePositionChange = (key, val) => {
    const parsed = parseFloat(val) || 0;
    if (key === "x" || key === "y") {
      updateElementState(selectedElement.id, {
        position: { ...selectedElement.state.position, [key]: parsed },
      });
    } else if (key === "w" || key === "h") {
      updateElementState(selectedElement.id, {
        size: { ...selectedElement.state.size, [key]: parsed },
      });
    } else {
      updateElementState(selectedElement.id, { [key]: parsed });
    }
  };

  const getAnimationsForElement = () => {
    const list = [];
    interactions.forEach((int) => {
      int.actions.forEach((act) => {
        if (act.element_id === selectedElement.id) {
          list.push({
            ...act,
            actionId: act.id,
            event: int.event,
            target: int.target,
          });
        }
      });
    });
    return list;
  };

  const handleAddNewAction = () => {
    addAction(selectedEvent, selectedTarget, {
      element_id: selectedElement.id,
      type: selectedActionType,
      to: buildToValue(),
      duration: parseFloat(duration) / 1000,
      delay: 0,
      ease,
    });
  };

  const handleUpdateAction = () => {
    updateAction(selectedActionId, {
      type: selectedActionType,
      to: buildToValue(),
      duration: parseFloat(duration) / 1000,
      ease,
    });

    setSelectedActionId(null);
  };

  const elementAnimations = getAnimationsForElement();

  return (
    <div
      className="w-full h-full bg-white flex flex-col overflow-y-auto"
      style={{ scrollbarWidth: "thin" }}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
        <div className="flex-1 min-w-0 mr-2">
          {selectedElement.type === "text" ? (
            <input
              value={selectedElement.text || ""}
              onChange={(e) =>
                updateElement(selectedElement.id, {
                  text: e.target.value,
                  name: e.target.value || "Text",
                })
              }
              className="text-sm font-semibold text-gray-800 bg-transparent border-b border-transparent hover:border-purpleColor focus:border-purpleColor outline-none w-full truncate"
              placeholder="Text content..."
            />
          ) : (
            <h4 className="text-sm font-semibold text-gray-800 truncate">
              {selectedElement.name}
            </h4>
          )}
          <span className="text-[11px] text-gray-400 capitalize">
            {selectedElement.type} element
          </span>
        </div>
        <div className="flex gap-2 text-gray-400 shrink-0">
          <button className="hover:text-gray-700 p-1">
            <Eye className="w-4 h-4" />
          </button>
          <button className="hover:text-gray-700 p-1">
            <Lock className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Text Style (text elements only) */}
      {selectedElement.type === "text" && (
        <div className="border-b border-gray-100">
          <button
            onClick={() => toggle("textStyle")}
            className="w-full p-3 flex justify-between items-center text-xs font-bold text-gray-600 uppercase"
          >
            Text Style
            {open.textStyle ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {open.textStyle && (
            <div className="px-4 pb-4 grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="text-gray-400 block mb-1">Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElement.color || "#ffffff"}
                    onChange={(e) => updateElement(selectedElement.id, { color: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5 shrink-0"
                  />
                  <input
                    type="text"
                    value={selectedElement.color || "#ffffff"}
                    onChange={(e) => updateElement(selectedElement.id, { color: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Font Size (px)</label>
                <input
                  type="number"
                  min="8"
                  value={selectedElement.fontSize || 28}
                  onChange={(e) => {
                    const newFontSize = parseInt(e.target.value, 10) || 28;
                    updateElement(selectedElement.id, { fontSize: newFontSize });
                    // The box is vertically centered, so text taller than the box balloons
                    // off-canvas in both directions. Grow the box to keep pace with the font.
                    const minHeight = Math.round(newFontSize * 1.4);
                    if (selectedElement.state.size.h < minHeight) {
                      updateElementState(selectedElement.id, {
                        size: { ...selectedElement.state.size, h: minHeight },
                      });
                    }
                  }}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Border Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElement.borderColor || "#d1d5db"}
                    onChange={(e) => updateElement(selectedElement.id, { borderColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5 shrink-0"
                  />
                  <input
                    type="text"
                    value={selectedElement.borderColor || "#d1d5db"}
                    onChange={(e) => updateElement(selectedElement.id, { borderColor: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Background Color</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={selectedElement.backgroundColor || "#ffffff"}
                    onChange={(e) => updateElement(selectedElement.id, { backgroundColor: e.target.value })}
                    className="w-8 h-8 rounded border border-gray-200 cursor-pointer p-0.5 shrink-0"
                  />
                  <input
                    type="text"
                    placeholder="rgba(255,255,255,0.5)"
                    value={selectedElement.backgroundColor || ""}
                    onChange={(e) => updateElement(selectedElement.id, { backgroundColor: e.target.value })}
                    className={inputCls}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 1. Position & Size */}
      <div className="border-b border-gray-100">
        <button
          onClick={() => toggle("posSize")}
          className="w-full p-3 flex justify-between items-center text-xs font-bold text-gray-600 uppercase"
        >
          Position & Size
          {open.posSize ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
        {open.posSize && (
          <div className="px-4 pb-4 grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="text-gray-400 block mb-1">X Position</label>
              <input
                type="number"
                value={selectedElement.state.position.x}
                onChange={(e) => handlePositionChange("x", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Y Position</label>
              <input
                type="number"
                value={selectedElement.state.position.y}
                onChange={(e) => handlePositionChange("y", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Width (W)</label>
              <input
                type="number"
                value={selectedElement.state.size.w}
                onChange={(e) => handlePositionChange("w", e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-gray-400 block mb-1">Height (H)</label>
              <input
                type="number"
                value={selectedElement.state.size.h}
                onChange={(e) => handlePositionChange("h", e.target.value)}
                className={inputCls}
              />
            </div>
            <div className="col-span-2">
              <label className="text-gray-400 block mb-1">Rotation</label>
              <input
                type="text"
                value={`${selectedElement.state.rotation || 0}°`}
                onChange={(e) =>
                  handlePositionChange(
                    "rotation",
                    e.target.value.replace("°", ""),
                  )
                }
                className={inputCls}
              />
            </div>
          </div>
        )}
      </div>

      {/* 2. Active Animations */}
      <div className="border-b border-gray-100">
        <div className="w-full p-3 flex justify-between items-center text-xs font-bold text-gray-600 uppercase">
          <span>Active Animations</span>
          <button
            onClick={() => toggle("animation")}
            className="text-gray-400 hover:text-purpleColor"
          >
            {open.animation ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>
        {open.animation && (
          <div className="px-4 pb-4 flex flex-col gap-2 text-xs">
            {elementAnimations.length === 0 ? (
              <p className="text-gray-400 italic text-center py-2">
                No animations yet. Add one below.
              </p>
            ) : (
              elementAnimations.map((anim) => (
                <div
                  key={anim.actionId}
                  className={`flex items-center justify-between p-2.5 rounded border group cursor-pointer transition ${
                    selectedActionId === anim.actionId
                      ? "bg-purpleColor/15 border-purpleColor/40"
                      : "bg-purpleColor/5 border-purpleColor/10 hover:bg-purpleColor/10"
                  }`}
                  onClick={() =>
                    setSelectedActionId(
                      selectedActionId === anim.actionId ? null : anim.actionId,
                    )
                  }
                >
                  <div className="flex flex-col">
                    <span className="font-semibold text-gray-700 capitalize">
                      {anim.type} ({anim.event})
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {anim.duration}s | Delay: {anim.delay}s
                    </span>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedActionId(anim.actionId);
                      }}
                      className="text-gray-400 hover:text-purpleColor p-0.5"
                      title="Edit action"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedActionId === anim.actionId)
                          setSelectedActionId(null);
                        deleteAction(anim.actionId);
                      }}
                      className="text-gray-400 hover:text-red-500 p-0.5"
                      title="Delete action"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* 3. Trigger */}
      <div className="border-b border-gray-100">
        <button
          onClick={() => toggle("trigger")}
          className="w-full p-3 flex justify-between items-center text-xs font-bold text-gray-600 uppercase"
        >
          Trigger
          {open.trigger ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
        {open.trigger && (
          <div className="px-4 pb-4 flex flex-col gap-3 text-xs">
            <div>
              <label className="text-gray-400 block mb-1">Event Type</label>
              <select
                value={selectedEvent}
                onChange={(e) => {
                  setSelectedEvent(e.target.value);
                  setSelectedTarget(
                    e.target.value === "slide_start" ||
                      e.target.value === "slide_unload"
                      ? "timeline"
                      : elements[0]?.id || "button_1",
                  );
                }}
                className={inputCls}
              >
                <option value="click">On Click</option>
                <option value="hover">On Hover</option>
                <option value="slide_start">On Slide Start</option>
                <option value="slide_unload">On Slide Unload</option>
              </select>
            </div>
            {selectedEvent !== "slide_start" &&
              selectedEvent !== "slide_unload" && (
                <div>
                  <label className="text-gray-400 block mb-1">
                    From Element
                  </label>
                  <select
                    value={selectedTarget}
                    onChange={(e) => setSelectedTarget(e.target.value)}
                    className={inputCls}
                  >
                    {elements.map((el) => (
                      <option key={el.id} value={el.id}>
                        {el.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
          </div>
        )}
      </div>

      {/* 4. Action Properties */}
      <div className="border-b border-gray-100">
        <div className="w-full p-3 flex justify-between items-center text-xs font-bold text-gray-600 uppercase">
          <div className="flex items-center gap-2">
            <span>{editingAction ? "Edit Action" : "Action Properties"}</span>
            {editingAction && (
              <span className="text-[10px] font-normal text-purpleColor bg-purpleColor/10 px-1.5 py-0.5 rounded-full capitalize">
                {editingAction.type}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {editingAction && (
              <button
                onClick={() => setSelectedActionId(null)}
                className="text-gray-400 hover:text-red-500 p-1 transition"
                title="Cancel editing"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => toggle("action")}
              className="text-gray-400 hover:text-purpleColor p-1"
            >
              {open.action ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
        {open.action && (
          <div className="px-4 pb-4 flex flex-col gap-3 text-xs">
            <div>
              <label className="text-gray-400 block mb-1">Action Type</label>
              <select
                value={selectedActionType}
                onChange={(e) => setSelectedActionType(e.target.value)}
                className={inputCls}
              >
                {Object.entries(animationConfig).map(([key, config]) => (
                  <option key={key} value={key}>
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              {animationConfig[selectedActionType].fields.map((field) => (
                <div key={field.key} className="col-span-2">
                  <label className="text-gray-400 block mb-1">
                    {field.label}
                  </label>

                  <input
                    type={field.type || "number"}
                    step={field.step}
                    value={actionValues[field.key] ?? ""}
                    onChange={(e) =>
                      setActionValues((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className={inputCls}
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-gray-400 block mb-1">
                  Duration (ms)
                </label>
                <input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-gray-400 block mb-1">Ease</label>
                <select
                  value={ease}
                  onChange={(e) => setEase(e.target.value)}
                  className={inputCls}
                >
                  <option value="Power1.out">Power1.out</option>
                  <option value="Power2.out">Power2.out</option>
                  <option value="Power3.out">Power3.out</option>
                  <option value="Linear.easeNone">Linear</option>
                  <option value="Bounce.easeOut">Bounce</option>
                </select>
              </div>
            </div>

            {editingAction ? (
              <div className="mt-1 flex gap-2">
                <button
                  onClick={handleUpdateAction}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-purpleColor hover:bg-[#7a1b51] text-white rounded-lg p-2 text-xs font-semibold transition"
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Update Action
                </button>
                <button
                  onClick={() => setSelectedActionId(null)}
                  className="flex items-center justify-center gap-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg px-3 p-2 text-xs font-semibold transition"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={handleAddNewAction}
                className="mt-1 w-full flex items-center justify-center gap-1.5 bg-purpleColor hover:bg-[#7a1b51] text-white rounded-lg p-2 text-xs font-semibold transition"
              >
                <Plus className="w-4 h-4" />
                Add Action
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
